import { z } from "zod";

/**
 * One entry per fixed marketplace (exactly 3 — shopee/lazada/tiktok, no
 * dynamic list per the grilling-session decision). `checked` = this
 * marketplace is part of the multi-channel creation; `seller_id` is
 * required exactly when checked, since sellers are opaque per-marketplace
 * shop identifiers with no cross-marketplace identity (see the Seller
 * glossary entry) — it can never be inferred from another channel's choice.
 */
const channelTargetSchema = z.object({
  checked: z.boolean(),
  seller_id: z.string().trim(),
});

export const multiChannelTargetsSchema = z
  .object({
    shopee: channelTargetSchema,
    lazada: channelTargetSchema,
    tiktok: channelTargetSchema,
  })
  .superRefine((targets, ctx) => {
    const entries = Object.entries(targets) as Array<
      [keyof typeof targets, { checked: boolean; seller_id: string }]
    >;
    const checkedCount = entries.filter(([, v]) => v.checked).length;
    if (checkedCount === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["shopee"],
        message: "promotion:create.err_at_least_one_channel",
      });
    }
    for (const [key, value] of entries) {
      if (value.checked && value.seller_id === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key, "seller_id"],
          message: "promotion:builder.err_seller_required",
        });
      }
    }
  });

export type MultiChannelTargets = z.infer<typeof multiChannelTargetsSchema>;

/** The default (all unchecked) shape RHF hydrates the form with. */
export const emptyMultiChannelTargets: MultiChannelTargets = {
  shopee: { checked: false, seller_id: "" },
  lazada: { checked: false, seller_id: "" },
  tiktok: { checked: false, seller_id: "" },
};
